import { Autocomplete, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, List, ListItem, ListItemButton, ListItemText, Switch, TextField, Typography, useTheme } from "@mui/material";
import { Fragment, useEffect, useState } from "react";
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import { setSnackbarMessage } from "../redux/snackbarMessageSlice";
import { useDispatch } from "react-redux";
import ConfirmDialogReuse from "../pages/Editor/ReuseComponents/ConfirmDialogReuse";
import { LoadingButton } from "@mui/lab";
import { setSimulatorProfiles } from "../redux/simulateMgtSlice";
import ConfirmDialog from "./ConfirmDialog";

const isEmptyRequiredValue = (value) => {
    if (Array.isArray(value)) {
        if (value.length === 0) {
            return true;
        }
        return value.every((item) => `${item ?? ''}`.trim() === '');
    }

    if (typeof value === 'boolean') {
        return false;
    }

    if (value === null || value === undefined) {
        return true;
    }

    if (typeof value === 'number') {
        return Number.isNaN(value);
    }

    return `${value}`.trim() === '';
};

const UserProfileManagement = (props) => {

    const { open, setOpen, project, onSaveProfile, usedSubjectAttributes, onAddNewProfile, initialProfileId, onCloneProfile } = props;

    const theme = useTheme();
    const dispatch = useDispatch();

    const [selectedIndex, setSelectedIndex] = useState(null);
    const [selectedProfile, setSelectedProfile] = useState(null);
    const [originalProfiles, setOriginalProfiles] = useState([]);
    const [isModified, setIsModified] = useState(false);
    const [openDeleteConfirm, setOpenDeleteConfirm] = useState(false);
    const [selectedDeleteProfile, setSelectedDeleteProfile] = useState(null);
    const [loading, setLoading] = useState(false);
    const [duplicateNameError, setDuplicateNameError] = useState(false);

    useEffect(() => {
        async function fetchProfiles() {
            try {
                const response = await fetch(`${process.env.REACT_APP_API_SIM_URL}/projects/${project?.project_id}/profiles`);
                if (response.ok) {
                    const result = await response.json();
                    setOriginalProfiles(result.profiles);
                    if (initialProfileId) {
                        const targetProfile = result.profiles.find(profile => profile.id === initialProfileId);
                        setSelectedIndex(initialProfileId);
                        setSelectedProfile(sortedAttributesProfile(targetProfile));
                    } else {
                        if (result.profiles.length > 0) {
                            setSelectedIndex(result.profiles[0].id);
                            setSelectedProfile(sortedAttributesProfile(result.profiles[0]));
                        } else {
                            setSelectedIndex(null);
                            setSelectedProfile(null);
                        }
                    }
                } else {
                    throw new Error('Failed to fetch profiles');
                }
            } catch (error) {
                console.error(error);
                dispatch(setSnackbarMessage({
                    open: true,
                    message: error,
                    status: "error",
                }));
            }
        }
        if (open) {
            setDuplicateNameError(false);
            fetchProfiles();
        }
    }, [open, initialProfileId])

    const sortedAttributesProfile = (profile) => {
        const attributes = profile.attributes;

        const sortedKeys = Object.keys(attributes).sort((a, b) => {
            const hasA = Object.hasOwn(usedSubjectAttributes, a);
            const hasB = Object.hasOwn(usedSubjectAttributes, b);

            if (hasA && !hasB) return -1;
            if (!hasA && hasB) return 1;
            return a.localeCompare(b);
        });

        const sortedAttributes = Object.fromEntries(
            sortedKeys.map(key => [key, attributes[key]])
        );

        return {
            ...profile,
            attributes: sortedAttributes
        };
    };

    const handleListItemClick = (item, index) => {
        setSelectedIndex(item.id);
        const sortedKeys = Object.keys(item.attributes).sort();
        const sortedAttributes = sortedKeys.reduce((acc, key) => {
            acc[key] = item.attributes[key];
            return acc;
        }, {});
        const updatedItem = {
            ...item,
            attributes: sortedAttributes
        }
        setSelectedProfile(sortedAttributesProfile(updatedItem));
        setDuplicateNameError(false);
        setIsModified(false);
    };

    const handleDelete = (id) => {
        const seletedProfile = originalProfiles.find(profile => profile.id === id);
        setSelectedDeleteProfile(seletedProfile);
        setOpenDeleteConfirm(true);
        // setSelectedIndex(null);
        // setSelectedProfile(null);
    };

    const onDeleteClose = () => {
        // setOpenDeleteConfirm(false);
        setSelectedDeleteProfile(null);
        console.log("Delete action cancelled.");
    }

    const onDeleteProfile = async () => {
        try {
            setLoading(true);
            let options = {
                method: 'DELETE',
                headers: {
                    'Content-type': 'application/json',
                },
                body: JSON.stringify({
                    profile_ids: [selectedDeleteProfile.id]
                })
            }
            const response = await fetch(`${process.env.REACT_APP_API_SIM_URL}/projects/${project?.project_id}/profiles`, options);
            if (response.ok) {
                const updatedProfiles = originalProfiles.filter(profile => profile.id !== selectedDeleteProfile.id);
                const notHaveSelected = !updatedProfiles.find(profile => profile.id === selectedIndex);
                if (notHaveSelected) {
                    setSelectedIndex(null);
                    setSelectedProfile(null);
                }
                setOriginalProfiles(updatedProfiles);
                dispatch(setSimulatorProfiles(updatedProfiles));
                setLoading(false);
                setOpenDeleteConfirm(false);
            } else {
                throw response;
            }
        } catch (error) {
            setLoading(false);
            setOpenDeleteConfirm(false);
            console.error("Error save profile: ", error);
            dispatch(setSnackbarMessage({ message: 'Failed to saved profile.', status: 'error' }));
        }
    };

    const handleClose = () => {
        setSelectedIndex(null);
        setSelectedProfile(null);
        setOriginalProfiles([]);
        setIsModified(false);
        // ตรวจสอบว่า profiles changed ไหม ถ้า changed เรียก get profiles
        setOpen(false);
    };

    const handleValueChange = (attrKey, newValue) => {
        if (!selectedProfile) {
            return;
        }

        let newDrafts = { ...selectedProfile };
        if (attrKey === 'profile-name') {
            const normalizedName = `${newValue ?? ''}`.trim();
            const nameExists = originalProfiles.some(
                (pf) => `${pf?.name ?? ''}`.trim() === normalizedName && pf.id !== selectedProfile.id
            );
            if (nameExists) {
                setDuplicateNameError(true);
            } else {
                setDuplicateNameError(false);
            }
            newDrafts = {
                ...newDrafts,
                name: newValue
            };
        } else {
            newDrafts = {
                ...newDrafts,
                attributes: {
                    ...newDrafts.attributes,
                    [attrKey]: newValue
                }
            };
        }
        const originalProfile = originalProfiles.find((profile) => profile.id === selectedProfile.id) || null;
        const is_modified = JSON.stringify(newDrafts) !== JSON.stringify(originalProfile);
        setIsModified(is_modified);
        setSelectedProfile(newDrafts);
    };

    const displayedAttributes = Object.entries(selectedProfile?.attributes ?? {}).filter(([attrKey]) => Object.hasOwn(usedSubjectAttributes, attrKey));
    const isProfileNameEmpty = `${selectedProfile?.name ?? ''}`.trim() === '';
    const hasEmptyRequiredAttribute = displayedAttributes.some(([, attrValue]) => isEmptyRequiredValue(attrValue));
    const hasValidationError = duplicateNameError || isProfileNameEmpty || hasEmptyRequiredAttribute;
    const isSaveDisabled = !isModified || hasValidationError;

    const handleSave = () => {
        if (isSaveDisabled || !selectedProfile) {
            return;
        }
        setIsModified(false);
        const updatedProfile = {
            ...selectedProfile,
            updated_at: new Date().toISOString()
        };
        if (onSaveProfile) {
            onSaveProfile(updatedProfile);
        }
        const updatedProfiles = originalProfiles.map((profile, index) => {
            if (profile.id === updatedProfile.id) {
                return updatedProfile;
            }
        });
        setOriginalProfiles((prev) => {
            const prevProfiles = [...prev];
            const updatedProfiles = prevProfiles.map((profile, index) => {
                if (profile.id === updatedProfile.id) {
                    return updatedProfile;
                }
                return profile;
            });
            return updatedProfiles;
        });
        // setOriginalProfiles((prev) => {
        //     const next = [...prev];
        //     next[selectedIndex] = updatedProfile;
        //     return next;
        // });
    };

    return (
        <Fragment>
            <Dialog
                fullWidth={true}
                maxWidth={'lg'}
                open={open}
            >
                <DialogTitle
                    sx={{ backgroundColor: theme.palette.primary.main, color: theme.palette.white.main, paddingY: 1, paddingX: 2 }}
                    className='flex justify-between items-center'
                >
                    <Typography>Profile Management</Typography>
                    <IconButton
                        aria-label="close"
                        onClick={handleClose}
                        sx={{
                            padding: 0,
                            color: theme.palette.white.main
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent
                    sx={{
                        height: '80vh',
                        padding: 0,
                        display: 'flex',
                        width: '100%',
                        overflow: 'hidden',
                    }}
                >
                    <Box sx={{ width: '25%' }}>
                        <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            paddingX: 2,
                            paddingY: 1,
                            backgroundColor: 'whitesmoke',
                            borderBottom: '1px solid #e0e0e0'
                        }}>
                            <Typography>Profiles</Typography>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                                <Button
                                    variant="outlined"
                                    size="small"
                                    color="inherit"
                                    sx={{ width: '50px', height: '25px' }}
                                    onClick={onAddNewProfile}
                                >
                                    New
                                </Button>
                                <Button
                                    variant="outlined"
                                    size="small"
                                    color="inherit"
                                    sx={{ width: '50px', height: '25px' }}
                                    disabled={selectedIndex == null}
                                    onClick={() => onCloneProfile(selectedProfile)}
                                >
                                    Clone
                                </Button>
                            </Box>
                        </Box>
                        <Box>
                            <List
                                sx={{ width: '100%', bgcolor: 'background.paper' }}
                                disablePadding
                            >
                                {originalProfiles.map((item, index) => (
                                    <ListItem
                                        key={item.id}
                                        disablePadding
                                        secondaryAction={
                                            <IconButton edge="end" aria-label="delete" onClick={() => handleDelete(item.id)}>
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        }
                                    >
                                        <ListItemButton
                                            selected={selectedIndex === item.id}
                                            onClick={() => handleListItemClick(item, index)}
                                        >
                                            <ListItemText primary={
                                                <Typography sx={{ color: selectedIndex === item.id ? 'primary.main' : 'GrayText' }}>
                                                    {item.name}
                                                </Typography>
                                            } />
                                        </ListItemButton>
                                    </ListItem>
                                ))}
                            </List>
                        </Box>
                    </Box>
                    <Box sx={{
                        width: '75%',
                        borderLeft: '1px solid #e0e0e0',
                        height: '100%',
                        position: 'relative',
                        overflowY: 'auto',
                        display: 'flex',
                        flexDirection: 'column'
                    }}>
                        {selectedProfile && selectedProfile.attributes && (
                            <>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'flex-end',
                                        p: 2,
                                        paddingBottom: 1,
                                        position: 'sticky',
                                        top: 0,
                                        zIndex: 10,
                                        backgroundColor: '#ffffff', // ใส่สีขาว
                                        width: '100%',             // [สำคัญ] บังคับให้กว้างเต็มพื้นที่ 75%
                                        boxSizing: 'border-box',    // ป้องกันไม่ให้ padding ทำให้กว้างเกิน 100%
                                        // borderBottom: '1px solid #e0e0e0', // เพิ่มเส้นตัดให้ดูชัดเจนเวลา scroll
                                    }}
                                >
                                    <Button
                                        variant="contained"
                                        size="small"
                                        disabled={isSaveDisabled}
                                        onClick={handleSave}
                                    >Save</Button>
                                </Box>
                                <Box sx={{ p: 1 }}>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            p: 1,
                                            ml: 3,
                                        }}
                                    >
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                // color: 'text.secondary',
                                                // fontWeight: 500,
                                                minWidth: '150px', // กำหนดความกว้างขั้นต่ำ (ปรับตัวเลขตามความยาวชื่อ key)
                                                flexShrink: 0,     // ป้องกันไม่ให้ label ถูกบีบจนตัวอักษรตัด
                                                mr: 2,             // ระยะห่าง (Gap) ระหว่าง Label กับ Input
                                            }}
                                        >
                                            Profile name
                                        </Typography>
                                        <TextField
                                            size="small"
                                            variant="outlined"
                                            value={selectedProfile.name ?? ''}
                                            onChange={(e) => {
                                                handleValueChange('profile-name', e.target.value);
                                            }}
                                            sx={{
                                                flexGrow: 1,
                                                '& .MuiInputBase-root': {
                                                    height: '32px',
                                                    fontSize: 12,
                                                },
                                                '& .MuiInputBase-input': {
                                                    padding: '0px 6px',
                                                },
                                            }}
                                            error={duplicateNameError || isProfileNameEmpty}
                                            helperText={duplicateNameError ? 'Profile name already exists.' : (isProfileNameEmpty ? 'Profile name cannot be empty.' : '')}
                                        />
                                    </Box>
                                    {displayedAttributes
                                        .map(
                                            ([attrKey, attrValue]) => {
                                                const isArray = Array.isArray(attrValue);
                                                const valueType = isArray ? 'array' : typeof attrValue;
                                                const fieldId = `${selectedProfile.id}-${attrKey}`;

                                                const isRequired = true;
                                                const hasAttributeError = isEmptyRequiredValue(attrValue);

                                                return (
                                                    <Box
                                                        key={fieldId}
                                                        sx={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            p: 1,
                                                            ml: 3,
                                                        }}
                                                    >
                                                        <Typography
                                                            variant="body2"
                                                            sx={{
                                                                // color: isRequired ? theme.palette.primary.main : 'text.secondary',
                                                                minWidth: '150px', // กำหนดความกว้างขั้นต่ำ (ปรับตัวเลขตามความยาวชื่อ key)
                                                                flexShrink: 0,     // ป้องกันไม่ให้ label ถูกบีบจนตัวอักษรตัด
                                                                mr: 2,             // ระยะห่าง (Gap) ระหว่าง Label กับ Input
                                                                // fontWeight: isRequired ? 700 : 500,
                                                            }}
                                                        >
                                                            {attrKey}
                                                        </Typography>

                                                        {/* กรณี Array: ใช้ Autocomplete Multiple FreeSolo */}
                                                        {isArray ? (
                                                            <Autocomplete
                                                                multiple
                                                                freeSolo
                                                                size='small'
                                                                options={[]} // ไม่มี predefined options ให้พิมพ์เอาเอง
                                                                value={Array.isArray(attrValue) ? attrValue : []}
                                                                onChange={(event, newValue) =>
                                                                    handleValueChange(attrKey, newValue)
                                                                }
                                                                renderValue={(tagValue, getTagProps) =>
                                                                    tagValue.map((option, index) => {
                                                                        const { key, ...tagProps } = getTagProps({ index });
                                                                        return (
                                                                            <Chip
                                                                                label={option}
                                                                                key={key}
                                                                                {...tagProps}
                                                                                size="small"
                                                                                variant="outlined"
                                                                                color={isRequired ? "primary" : "default"}
                                                                                sx={{
                                                                                    // color: isRequired ? theme.palette.primary.main : "",
                                                                                    borderColor: isRequired ? theme.palette.primary.main : "",
                                                                                }}
                                                                            />
                                                                        )
                                                                    })
                                                                }
                                                                renderInput={(params) => (
                                                                    <TextField
                                                                        {...params}
                                                                        variant="outlined"
                                                                        placeholder="Add items..."
                                                                        error={hasAttributeError}
                                                                        sx={{
                                                                            width: '100%',
                                                                            fontSize: 12,
                                                                            '& .MuiInputBase-input': {
                                                                                padding: '0px 6px',
                                                                            },
                                                                            '& .MuiInputBase-input::placeholder': {
                                                                                fontSize: '12px',
                                                                            },
                                                                            // input: {
                                                                            //     color: isRequired ? theme.palette.primary.main : '',
                                                                            // },
                                                                        }}
                                                                    />
                                                                )}
                                                                sx={{ flexGrow: 1 }}
                                                            />
                                                        ) : valueType === 'boolean' ? (
                                                            <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center' }}>
                                                                <Switch
                                                                    size="small"
                                                                    checked={attrValue}
                                                                    onChange={(e) => handleValueChange(attrKey, e.target.checked)}
                                                                />
                                                            </Box>
                                                        ) : (
                                                            <TextField
                                                                size="small"
                                                                variant="outlined"
                                                                value={attrValue ?? ''}
                                                                error={hasAttributeError}
                                                                onChange={(e) => {
                                                                    let val = e.target.value;
                                                                    if (valueType === 'number') val = val === '' ? '' : Number(val);
                                                                    handleValueChange(attrKey, val);
                                                                }}
                                                                sx={{
                                                                    flexGrow: 1,
                                                                    '& .MuiInputBase-root': {
                                                                        height: '32px',
                                                                        fontSize: 12,
                                                                    },
                                                                    '& .MuiInputBase-input': {
                                                                        padding: '0px 6px',
                                                                    },
                                                                    // input: {
                                                                    //     color: isRequired ? theme.palette.primary.main : '',
                                                                    // },
                                                                }}
                                                            />
                                                        )}
                                                    </Box>
                                                );
                                            }
                                        )}
                                </Box>
                            </>
                        )}
                    </Box>
                </DialogContent>
            </Dialog>
            {/* <ConfirmDialogReuse
                open={openDeleteConfirm}
                maxWidth={"sm"}
                content={
                    <div>
                        <Typography variant="subtitle1">Are you sure you want to delete profile "{selectedDeleteProfile?.name}" ?</Typography>
                    </div>
                }
                actions={
                    <div className='grid grid-cols-2 gap-2'>
                        <LoadingButton
                            variant="contained"
                            loading={loading}
                            onClick={() => onDeleteProfile()}
                        >
                            Confirm
                        </LoadingButton>
                        <Button
                            disabled={loading}
                            variant="outlined"
                            onClick={() => onDeleteClose()}
                        >
                            Cancel
                        </Button>
                    </div>
                }
            /> */}
            <ConfirmDialog
                open={openDeleteConfirm}
                setOpen={setOpenDeleteConfirm}
                title="Delete"
                description={`Are you sure you want to delete profile "${selectedDeleteProfile?.name}" ?`}
                onConfirm={onDeleteProfile}
                onClose={onDeleteClose}
            />
        </Fragment>
    );
};

export default UserProfileManagement;